import { Doctor, DoctorDocumentTypeContext } from "@common/utils/types";
import { createSlice } from "@reduxjs/toolkit";
import { documentSelected, historyDocumentSelected } from "./documentSlice";
import { RootState } from "@/app/store";

/**
 * Get the selected doctor from local storage
 * @returns {Doctor | null}
 * */
const getLocalDoctor = () => {
    const doctor = localStorage.getItem("selectedDoctor");
    if (doctor) {
        return JSON.parse(doctor);
    }
    return null;
};

/**
 * Get the selected doctors from local storage
 * @returns {Doctor[]}
 * */
const getLocalDoctors = () => {
    const doctors = localStorage.getItem("selectedDoctors");
    if (doctors) {
        return JSON.parse(doctors);
    }
    return [];
};

const initialState: {
    selectedDoctor: Doctor | null;
    selectedDoctors: Doctor[];
} = {
    selectedDoctor: getLocalDoctor(),
    selectedDoctors: getLocalDoctors(), // Initialize from localStorage
};

/**
 * Switch the selected doctor and update the selected documents that are saved for the doctor
 * This function should be called with a doctor from React Query cache
 * @param doctor - The doctor object (or null for "all doctors")
 * */
export const switchDoctor = (doctor: Doctor | null) => {
    return (dispatch: any, getState: () => RootState) => {
        // get the selected documents
        const state = getState();
        const selectedDocuments = state.documents.selectedDocuments;

        // null doctor means all doctors. Set the selected documents to whatever's in the state
        if (!doctor) {
            dispatch(doctorSelected(null));
            dispatch(documentSelected(selectedDocuments));
            return;
        }

        // set the selected doctor state
        dispatch(doctorSelected(doctor));

        // get the selected documents for the doctor and set the state
        const documentIds = doctor?.doctor_document_types
            ?.filter((doc) => doc.context === DoctorDocumentTypeContext.Default || !doc.context)
            .map((doc) => doc.document_type_id);
        if (documentIds && documentIds.length > 0) {
            dispatch(documentSelected(documentIds));
        } else {
            // if there are no documents for the doctor, set the selected documents to whatever's in state
            dispatch(documentSelected(selectedDocuments));
        }

        // get the selected history documents for the doctor and set the state
        const historyDocumentIds = doctor?.doctor_document_types
            ?.filter((doc) => doc.context === DoctorDocumentTypeContext.History)
            .map((doc) => doc.document_type_id);
        if (historyDocumentIds && historyDocumentIds.length > 0) {
            dispatch(historyDocumentSelected(historyDocumentIds));
        }
    };
};

const doctorsSlice = createSlice({
    name: "doctors",
    initialState,
    reducers: {
        doctorSelected(state, action) {
            state.selectedDoctor = action.payload;
            localStorage.setItem("selectedDoctor", JSON.stringify(action.payload));
        },
        doctorsSelected(state, action) {
            state.selectedDoctors = action.payload;
            // For backward compatibility, set the first doctor as selectedDoctor
            state.selectedDoctor = action.payload.length > 0 ? action.payload[0] : null;
            localStorage.setItem("selectedDoctors", JSON.stringify(action.payload));
        },
        // Narrows only the filter list. Unlike doctorsSelected it leaves selectedDoctor alone:
        // that is the active doctor (new-appointment default, document-type prefs, deep links),
        // not part of the filter, and must not be nulled just because the filter is (EN-1397).
        doctorFilterReconciled(state, action: { payload: Doctor[] }) {
            state.selectedDoctors = action.payload;
            localStorage.setItem("selectedDoctors", JSON.stringify(action.payload));
        },
        clearDoctorState(state) {
            state.selectedDoctor = null;
            state.selectedDoctors = [];
            localStorage.removeItem("selectedDoctor");
            localStorage.removeItem("selectedDoctors");
        },
    },
});

export const { doctorSelected, doctorsSelected, doctorFilterReconciled, clearDoctorState } = doctorsSlice.actions;

export default doctorsSlice.reducer;
